import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// COMPONENTS
import { SettingsKey } from '@app/core/_constant/settings.constants';
import { SettingsService } from '@app/core/settings/services/settings.service';

import { DialogConfig } from '../../../shared/configs/dialog.config';

// CONFIGS
import { PaginationConfig } from '../../../shared/configs/paginator.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
SnackBarConfig;

// INTERFACES
import { IPagination } from '../../../shared/interfaces/paginator.interface';

// SERVICES
import { ClientPaymentProfileApiService } from '../../../shared/services/api/client/client-payment-profile-api.service';
import { UiConfirmationDialogComponent } from '../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { ICreditCard } from '../../interfaces/client-payment-profile.interface';
import { ClientPaymentProfileFormComponent } from '../client-payment-profile-form/client-payment-profile-form.component';

//CONSTANTS

@Component({
  selector: 'lum-client-payment-profile-list',
  templateUrl: './client-payment-profile-list.component.html',
  styleUrls: ['./client-payment-profile-list.component.scss'],
})
export class ClientPaymentProfileListComponent implements OnInit, OnDestroy {
  @Input()
  client :any; // TODO: add interface

  paginationConfig = PaginationConfig;
  creditCard :ICreditCard | null = null;
  creditCards :ICreditCard[] = [];
  loading :boolean = false;
  params :{
    [param :string] :string | string[];
  } = {
    businessAccountId: '',
    page: '1',
    limit: '10',
  };
  pagination$ :Observable<IPagination>;
  noPaymentGatewaySettings :boolean = false;
  settingsKey :any = SettingsKey;
  toolTipMessage :string = '';

  private onDestroy$ = new Subject();

  constructor(
    private clientPaymentProfileApiService :ClientPaymentProfileApiService,
    private dialog :MatDialog,
    private snackbar :MatSnackBar,
    private settingsSvc :SettingsService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.initParams();
    this.initPaymentGatewaySettings();
    this.getDataFromService();
    this.getClientCreditCards();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onOpenCardForm() :void {
    this.dialog
      .open(ClientPaymentProfileFormComponent, {
        data: {
          creditCard: this.creditCard,
          client: this.client,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        if (res && res.id) {
          this.getClientCreditCards();
        }
        this.creditCard = null;
      });
  }

  onPaginationEvent(event :PageEvent) :void {
    this.params = {
      ...this.params,
      limit: event.pageSize.toString(),
      page: (event.pageIndex + 1).toString(),
    };

    this.getClientCreditCards();
  }

  onSetDefaultCard(card :ICreditCard) :void {
    this.clientPaymentProfileApiService
      .setDefault(card.id, {
        businessAccountId: this.client.id,
        userId: card.userId || this.client.user.id,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.snackbar.open(
              'Default payment profile sucessfully updated',
              'Close',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          }
        },
        error: (err) => {
          const { error } = err;
          this.snackbar.open(
            error || 'Unable to set as default payment profile',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  onManageCard(event :{ card :ICreditCard; type :string }) :void {
    const { card, type } = event;
    this.creditCard = card;

    if (type === 'edit') {
      this.onOpenCardForm();
    } else {
      this.onConfirmDeleteCard();
    }
  }

  private initParams() :void {
    this.params = {
      ...this.params,
      businessAccountId: String(this.client.id),
    };
  }

  private getDataFromService() :void {
    this.pagination$ = this.clientPaymentProfileApiService.pagination$;
  }

  private initPaymentGatewaySettings() :void {
    this.settingsSvc
      .getKeyValue(this.settingsKey.PAYMENT_GATEWAY)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (value) => {
          if (Object.keys(value)?.length === 0) {
            this.noPaymentGatewaySettings = true;
            this.toolTipMessage =
              'Cannot add a new card due to payment gateway configuration. Please contact support or try again later.';
          }
          this.cd.detectChanges();
        },
      });
  }

  private getClientCreditCards() :void {
    this.loading = true;
    this.clientPaymentProfileApiService
      .get(this.params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response :any) => {
          const { data } = response;
          this.creditCards = data;
        },
        error: (err) => {
          const { error } = err;
          this.snackbar.open(
            error ||
              'Unable to fetch payment profile cards. Please try again later.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private onConfirmDeleteCard() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Are you sure you want to delete this item?',
        },
        width: DialogConfig.sizes.small,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        if (res) {
          this.onDeleteCard();
        } else {
          this.creditCard = null;
        }
      });
  }

  private onDeleteCard() :void {
    this.clientPaymentProfileApiService
      .delete(this.creditCard?.id, this.defaultParams)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.creditCard = null)),
      )
      .subscribe({
        next: (res :any) => {
          const { success } = res;
          this.snackbar.open(success ?? 'Successfully removed.', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.getClientCreditCards();
        },
        error: (err :any) => {
          const { error } = err;
          this.snackbar.open(error ?? 'Unable to remove card profile.', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get defaultParams() :{ businessAccountId :number; userId :number } {
    return {
      businessAccountId: this.creditCard?.businessAccountId ?? this.client.id,
      userId: this.creditCard?.userId ?? this.client.user.id,
    };
  }
}
